import React, { useState, useEffect, useCallback, useRef } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import Dashboard from "./pages/Dashboard";
import DeviceList from "./pages/DeviceList";
import Forecast from "./pages/Forecast";
import LeadershipForecast from "./pages/LeadershipForecast";
import Settings from "./pages/Settings";
import Inventory from "./pages/Inventory";
import EapTlsMigration from "./pages/EapTlsMigration";
import Login from "./pages/Login";
import { parseLWTCSV, DEFAULT_PRICE, CAMPUS_MAP } from "./config/devices";
import { buildJiraMap } from "./config/jira";

const NAV = [
  { id: "dashboard", label: "Dashboard",  context: "4-Year Budgetary", icon: "◈", color: "var(--accent)" },
  { id: "devices",   label: "Devices",    context: "4-Year Budgetary", icon: "▤", color: "var(--accent)" },
  { id: "forecast",  label: "Forecast",   context: "4-Year Budgetary", icon: "◷", color: "var(--accent)" },
  // The former 4-year lifecycle is available only through the Settings archive.
  { id: "inventory", label: "Inventory",  context: "Asset Registry Cleanup", icon: "⊞", color: "var(--success)" },
  { id: "eaptls",    label: "EAP-TLS Migration", context: "SSID Readiness", icon: "◉", color: "var(--warn)" },
  { id: "settings",  label: "Settings",   context: "Config", icon: "◎", color: "var(--muted)" },
];
const PRIMARY_NAV = [
  { ...NAV[0], context: "5-Year Lifecycle" },
  { ...NAV[1], context: "5-Year Budgetary" },
  { ...NAV[2], context: "5-Year Budgetary" },
  NAV[3],
  NAV[4],
  NAV[5],
];

const STORAGE_KEY = "cwc_device_csv_v1";
const JIRA_KEY    = "cwc_jira_map_v1";
const PRICE_KEY   = "cwc_device_price";

const REVIEWER_MAP = {
  "cwict1@christway.vic.edu.au": "Tom Wan",
  "cwict2@hccviceduau.onmicrosoft.com": "Tristan Kirchner",
  "cwict3@hccviceduau.onmicrosoft.com": "Chris Bollhorst",
  "admin@hcc.vic.edu.au": "Absolute legend JV",
};

const BUDGET_DESKTOP_RE = /\bdesktop\b|small form factor|\bsff\b|\btiny\b|thinkcentre/i;
const CURRENT_REFRESH_YEAR = 2026;

function buildReviewer(account) {
  const email = (account?.username || account?.idTokenClaims?.preferred_username || "").toLowerCase();
  return {
    email,
    name: REVIEWER_MAP[email] || account?.name || email || "Unknown reviewer",
  };
}

function normalizeDefenderDevices(devices = {}) {
  return Object.fromEntries(
    Object.entries(devices)
      .filter(([serial]) => serial)
      .map(([serial, data]) => [serial.toUpperCase(), data])
  );
}

function isBudgetDesktop(device) {
  return (
    device?.deviceType === "Staff Device" &&
    BUDGET_DESKTOP_RE.test(String(device?.description || ""))
  );
}

function budgetEolYear(device, lifecycleYears = 4) {
  const ordered = new Date(device?.dateOrdered || "");
  const orderedYear = Number.isNaN(ordered.getTime()) ? null : ordered.getFullYear();
  const storedFourYearEol = Number(device?.eolYear) || null;
  const fourYearEol = orderedYear ? orderedYear + 4 : storedFourYearEol;

  if (!fourYearEol) return null;
  // The 2026 refresh has already been finalised and purchased under the
  // four-year programme. The ratified five-year cycle starts with the
  // remaining fleet and does not defer completed replacements into 2027.
  if (Number(lifecycleYears) === 5 && fourYearEol <= CURRENT_REFRESH_YEAR) return null;
  if (isBudgetDesktop(device) && fourYearEol <= 2025) return CURRENT_REFRESH_YEAR;
  if (fourYearEol <= 2025) return null;

  return orderedYear
    ? orderedYear + Number(lifecycleYears)
    : storedFourYearEol + Math.max(0, Number(lifecycleYears) - 4);
}

export default function App() {
  const isAuthenticated        = useIsAuthenticated();
  const { instance } = useMsal();
  const isLeadershipForecast = window.location.pathname.replace(/\/$/, "") === "/leadership-forecast";

  const [tab, setTab]           = useState("dashboard");
  const [allDevices, setAll]    = useState([]);
  const [jiraMap, setJiraMap]   = useState({});
  const [defenderMap, setDefenderMap]       = useState({});
  const [defenderLoading, setDefenderLoading] = useState(false);
  const [csvFiles, setCsvFiles]             = useState([]);
  const [jiraLoading, setJL]               = useState(false);
  const [jiraError, setJE]                 = useState(null);
  const [jiraLoadedAt, setJLA]             = useState(null);
  const [devicePrice, setDevicePrice]      = useState(() => {
    try { const v = localStorage.getItem(PRICE_KEY); return v ? Number(v) : DEFAULT_PRICE; }
    catch { return DEFAULT_PRICE; }
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState("");
  const [configLoadedAt, setConfigLoadedAt] = useState(null);
  const [lwtLoading, setLwtLoading]       = useState(false);
  const [lwtFallback, setLwtFallback]     = useState(false);
  const [lwtStatus, setLwtStatus]         = useState(null);
  const [lwtLoadedAt, setLwtLoadedAt]     = useState(null);
  const [lwtDeviceCount, setLwtDeviceCount] = useState(null);
  const hasAutoSynced   = useRef(false);
  const defenderMapRef  = useRef({});
  const [inventoryDevices, setInventoryDevices] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryLoadedAt, setInventoryLoadedAt] = useState(null);
  const [inventoryError, setInventoryError]     = useState(null);
  const [inventoryDefenderLoading, setInventoryDefenderLoading] = useState(false);
  const [inventoryDefenderProgress, setInventoryDefenderProgress] = useState(0);
  const [isReviewDirty, setIsReviewDirty] = useState(false);
  const [mdeSweepLoading, setMdeSweepLoading] = useState(false);
  const [mdeSweepProgress, setMdeSweepProgress] = useState(0);
  const [mdeSweepTotal, setMdeSweepTotal] = useState(0);
  const [mdeSweepFound, setMdeSweepFound] = useState(0);
  const [mdeSweepDone, setMdeSweepDone] = useState(false);

  const handlePriceChange = useCallback(async (val) => {
    const n = Number(val);
    if (!isNaN(n) && n > 0) {
      const price = Math.round(n);
      setConfigSaving(true);
      setConfigError("");
      try {
        const res = await fetch("/api/app-config", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ devicePrice: price }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        const sharedPrice = Number(data.config?.devicePrice) || price;
        setDevicePrice(sharedPrice);
        setConfigLoadedAt(new Date().toISOString());
        try { localStorage.setItem(PRICE_KEY, String(sharedPrice)); } catch { /* ignore */ }
        return true;
      } catch (e) {
        setConfigError(String(e.message || e));
        return false;
      } finally {
        setConfigSaving(false);
      }
    }
    setConfigError("Enter a valid device cost.");
    return false;
  }, []);

  // Load persisted data on mount
  useEffect(() => {
    setConfigLoading(true);
    fetch("/api/app-config")
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        const sharedPrice = Number(data.config?.devicePrice);
        if (Number.isFinite(sharedPrice) && sharedPrice > 0) {
          setDevicePrice(sharedPrice);
          try { localStorage.setItem(PRICE_KEY, String(sharedPrice)); } catch { /* ignore */ }
        }
        setConfigLoadedAt(new Date().toISOString());
        setConfigError("");
      })
      .catch(e => setConfigError(`Shared settings unavailable: ${e.message}`))
      .finally(() => setConfigLoading(false));

    // Hydrate from localStorage for instant first render
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const { devices, files } = JSON.parse(stored);
        setAll(devices || []);
        setCsvFiles(files || []);
      }
    } catch { /* ignore */ }

    // Primary: fetch live from LWT API
    setLwtLoading(true);
    fetch("/api/lwt-devices")
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        if (data?.devices?.length) {
          setAll(data.devices);
          setCsvFiles([]);
          setLwtLoadedAt(data.loadedAt || new Date().toISOString());
          setLwtDeviceCount(data.total ?? data.devices.length);
          setLwtFallback(Boolean(data.liveUnavailable));
          setLwtStatus({
            source: data.source || "LWT API",
            partial: Boolean(data.partial),
            liveUnavailable: Boolean(data.liveUnavailable),
            liveCount: Number(data.liveCount) || 0,
            cachedCount: Number(data.cachedCount) || 0,
            snapshotCount: Number(data.snapshotCount) || 0,
            failedOrderCount: Number(data.failedOrderCount) || 0,
          });
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ devices: data.devices, files: [] }));
          } catch { /* ignore */ }
        }
        setLwtLoading(false);
      })
      .catch(() => {
        // Fallback: blob storage CSV
        setLwtFallback(true);
        setLwtStatus({ partial: true, liveUnavailable: true, source: "Cached CSV fallback" });
        fetch("/api/lwt-csv")
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data?.devices?.length) {
              setAll(data.devices);
              setCsvFiles(data.files || []);
              try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({ devices: data.devices, files: data.files || [] }));
              } catch { /* ignore */ }
            }
            setLwtLoading(false);
          })
          .catch(() => { setLwtLoading(false); });
      });

    try {
      const jStored = localStorage.getItem(JIRA_KEY);
      if (jStored) {
        const { map, loadedAt } = JSON.parse(jStored);
        setJiraMap(map || {});
        setJLA(loadedAt);
      }
    } catch { /* ignore */ }
  }, []);

  // Enrich devices with Jira + Defender data
  const enrichedDevices = React.useMemo(() => {
    return allDevices.map(d => {
      const j        = jiraMap[d.serial?.toUpperCase()];
      const defender = defenderMap[d.serial?.toUpperCase()];
      return {
        ...d,
        ...(j ? {
          assignedTo: j.assignedTo || d.assignedTo,
          jiraKey:    j.jiraKey    || d.jiraKey,
          jiraStatus: j.status     || d.jiraStatus,
          jiraDescription: j.description || d.jiraDescription || null,
          hostname:   j.hostname   || d.hostname,
          ictNotes:   j.ictNotes   || d.ictNotes,
          campus: (j.location && CAMPUS_MAP[j.location.toLowerCase()])
            ? CAMPUS_MAP[j.location.toLowerCase()]
            : d.campus,
        } : {}),
        ...(defender ? {
          defenderLastUser:        defender.lastLogonUser        || null,
          defenderLastDisplayName: defender.lastLogonDisplayName || null,
          defenderLastLogon:       defender.lastLogonTime || defender.lastSeen || null,
          defenderDeviceName:      defender.deviceName           || null,
        } : {}),
      };
    });
  }, [allDevices, jiraMap, defenderMap]);

  const computeDevices = React.useMemo(
    () => enrichedDevices
      .filter(d =>
        d.isCompute &&
        d.deviceType === "Staff Device" &&
        budgetEolYear(d) > CURRENT_REFRESH_YEAR
      )
      .map(d => ({
        ...d,
        eolYear: budgetEolYear(d),
        sourceEolYear: d.eolYear,
        budgetCarryForward: isBudgetDesktop(d) && Number(d.eolYear) <= 2025,
      })),
    [enrichedDevices]
  );

  const computeDevices5Year = React.useMemo(
    () => enrichedDevices
      .filter(d =>
        d.isCompute &&
        d.deviceType === "Staff Device" &&
        budgetEolYear(d, 5)
      )
      .map(d => ({
        ...d,
        eolYear: budgetEolYear(d, 5),
        sourceEolYear: d.eolYear,
        budgetCarryForward: isBudgetDesktop(d) && Number(d.eolYear) <= 2025,
        lifecycleYears: 5,
      })),
    [enrichedDevices]
  );

  const staffDevicesForSync = React.useMemo(
    () => enrichedDevices
      .filter(d =>
        d.isCompute &&
        d.deviceType === "Staff Device" &&
        budgetEolYear(d)
      )
      .map(d => ({
        ...d,
        eolYear: budgetEolYear(d),
        sourceEolYear: d.eolYear,
        budgetCarryForward: isBudgetDesktop(d) && Number(d.eolYear) <= 2025,
      })),
    [enrichedDevices]
  );

  const handleCSV = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = e => {
      const parsed = parseLWTCSV(e.target.result);
      setAll(prev => {
        const map = {};
        prev.forEach(d => { map[d.serial] = d; });
        parsed.forEach(d => { map[d.serial] = d; });
        const merged = Object.values(map);
        const files = [...csvFiles.filter(f => f.name !== file.name), { name: file.name, count: parsed.length }];
        setCsvFiles(files);
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ devices: merged, files }));
        } catch { /* storage full */ }
        fetch("/api/lwt-csv", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ devices: merged, files }),
        }).catch(() => {});
        return merged;
      });
    };
    reader.readAsText(file);
  }, [csvFiles]);

  const clearData = useCallback(() => {
    setAll([]); setCsvFiles([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const loadJira = useCallback(async () => {
    setJL(true); setJE(null);
    try {
      const res = await fetch("/api/jira-assets");
      if (!res.ok) throw new Error(`Jira API error ${res.status}`);
      const data = await res.json();
      const map = buildJiraMap(data.assets || []);
      const loadedAt = new Date().toISOString();
      setJiraMap(map);
      setJLA(loadedAt);
      try {
        localStorage.setItem(JIRA_KEY, JSON.stringify({ map, loadedAt }));
      } catch { /* ignore */ }
    } catch (e) {
      setJE(e.message);
    } finally {
      setJL(false);
    }
  }, []);

  const clearJira = useCallback(() => {
    setJiraMap({}); setJLA(null);
    localStorage.removeItem(JIRA_KEY);
  }, []);

  const loadInventory = useCallback(() => {
    setInventoryLoading(true);
    setInventoryDevices([]);
    setInventoryLoadedAt(null);
    setInventoryError(null);
    setInventoryDefenderLoading(false);
    setInventoryDefenderProgress(0);
    const go = async (page, acc) => {
      try {
        const res = await fetch(`/api/jira-inventory?page=${page}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const next = [...acc, ...(data.assets || [])];
        setInventoryDevices([...next]);
        if (data.isLast) {
          setInventoryLoading(false);
          setInventoryLoadedAt(new Date().toISOString());
        } else {
          go(page + 1, next);
        }
      } catch (e) {
        setInventoryError(String(e));
        setInventoryLoading(false);
      }
    };
    go(0, []);
  }, []);

  // Auto-sync Jira once LWT data is available
  useEffect(() => {
    if (!hasAutoSynced.current && staffDevicesForSync.length > 0) {
      hasAutoSynced.current = true;
      loadJira();
    }
  }, [staffDevicesForSync.length, loadJira]);

  // Defender sync — calls Azure Function which uses app-only credentials
  const loadDefender = useCallback(async (devices) => {
    const serials = [...new Set(devices.map(d => d.serial).filter(Boolean))];
    if (!serials.length) return;
    setDefenderLoading(true);
    try {
      const res = await fetch("/api/defender-logons", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ serials }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const normalized = normalizeDefenderDevices(data.devices);
      defenderMapRef.current = { ...defenderMapRef.current, ...normalized };
      setDefenderMap(prev => ({ ...prev, ...normalized }));
    } catch { /* fail silently */ }
    finally { setDefenderLoading(false); }
  }, []);

  useEffect(() => {
    if (jiraLoadedAt && computeDevices.length > 0) {
      loadDefender(computeDevices);
    }
  }, [jiraLoadedAt]); // intentional — only re-run when Jira sync completes

  // Keep ref current so loadDefenderForInventory always checks fresh state
  useEffect(() => { defenderMapRef.current = defenderMap; }, [defenderMap]);

  // MDE Security Center sweep — second pass for serials still missing after Graph sync
  const loadMdeSweep = useCallback(async (missingSerials) => {
    if (!missingSerials.length) return;
    const BATCH = 5;
    setMdeSweepLoading(true);
    setMdeSweepProgress(0);
    setMdeSweepTotal(missingSerials.length);
    setMdeSweepFound(0);
    setMdeSweepDone(false);
    let processed = 0;
    const allMdeResults = {};
    for (let i = 0; i < missingSerials.length; i += BATCH) {
      const batch = missingSerials.slice(i, i + BATCH);
      try {
        const res = await fetch("/api/mde-sweep", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ serials: batch }),
        });
        if (res.ok) {
          const batchResult = await res.json();
          const normalized = normalizeDefenderDevices(batchResult.devices);
          const keys = Object.keys(normalized);
          if (keys.length) {
            Object.assign(allMdeResults, normalized);
            defenderMapRef.current = { ...defenderMapRef.current, ...normalized };
            setDefenderMap(prev => ({ ...prev, ...normalized }));
            setMdeSweepFound(Object.keys(allMdeResults).length);
          }
        }
      } catch { /* fail silently */ }
      processed += batch.length;
      setMdeSweepProgress(processed);
    }
    if (Object.keys(allMdeResults).length) {
      defenderMapRef.current = { ...defenderMapRef.current, ...allMdeResults };
      setDefenderMap(prev => ({ ...prev, ...allMdeResults }));
    }
    setMdeSweepLoading(false);
    setMdeSweepDone(true);
  }, []);

  // Defender sync for inventory — skips serials already in defenderMap
  const loadDefenderForInventory = useCallback(async (devices) => {
    const BATCH = 100;
    const allSerials = [...new Set(
      devices.map(d => d.serial).filter(Boolean).map(s => s.toUpperCase())
    )];
    let cachedMisses = [];
    try {
      const cacheRes = await fetch("/api/defender-cache", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ serials: allSerials }),
      });
      if (cacheRes.ok) {
        const cacheData = await cacheRes.json();
        const cachedDevices = normalizeDefenderDevices(cacheData.devices);
        cachedMisses = (cacheData.cachedMisses || []).map(s => s.toUpperCase());
        if (Object.keys(cachedDevices).length) {
          defenderMapRef.current = { ...defenderMapRef.current, ...cachedDevices };
          setDefenderMap(prev => ({ ...prev, ...cachedDevices }));
        }
        console.log(`[Defender/cache] hits=${Object.keys(cachedDevices).length} misses=${cachedMisses.length} requested=${allSerials.length}`);
      }
    } catch { /* cache miss/failure should not block live scans */ }

    const cachedMissSet = new Set(cachedMisses);
    const newSerials = allSerials.filter(s => !defenderMapRef.current[s] && !cachedMissSet.has(s));
    console.log(`[Defender/inventory] total=${allSerials.length} cached=${allSerials.length - newSerials.length} queuing=${newSerials.length}`);
    if (!newSerials.length) return;
    setInventoryDefenderLoading(true);
    setInventoryDefenderProgress(0);
    let processed = 0;
    let merged = 0;
    for (let i = 0; i < newSerials.length; i += BATCH) {
      const batch = newSerials.slice(i, i + BATCH);
      try {
        const res = await fetch("/api/defender-logons", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ serials: batch }),
        });
        if (!res.ok) { processed += batch.length; setInventoryDefenderProgress(processed); continue; }
        const data = await res.json();
        const normalized = normalizeDefenderDevices(data.devices);
        if (Object.keys(normalized).length) {
          const count = Object.keys(normalized).length;
          merged += count;
          defenderMapRef.current = { ...defenderMapRef.current, ...normalized };
          setDefenderMap(prev => ({ ...prev, ...normalized }));
          console.log(`[Defender/inventory] batch ${i}-${i + batch.length}: ${count} hits, total merged=${merged}`);
        }
      } catch { /* fail silently */ }
      processed += batch.length;
      setInventoryDefenderProgress(processed);
    }
    console.log(`[Defender/inventory] done — ${merged} devices found in Defender out of ${newSerials.length} queried`);
    setInventoryDefenderLoading(false);

    // Phase 3: MDE sweep for serials still not found
    const stillMissing = newSerials.filter(s => !defenderMapRef.current[s]);
    console.log(`[MDE sweep] ${stillMissing.length} serials still missing — starting MDE sweep`);
    if (stillMissing.length > 0) {
      await loadMdeSweep(stillMissing);
    }
  }, [loadMdeSweep]);

  useEffect(() => {
    if (inventoryLoadedAt && inventoryDevices.length > 0) {
      loadDefenderForInventory(inventoryDevices);
    }
  }, [inventoryLoadedAt]); // intentional — only re-run when inventory finishes loading

  useEffect(() => {
    if (isAuthenticated && !instance.getActiveAccount()) {
      const cachedAccount = instance.getAllAccounts()[0];
      if (cachedAccount) instance.setActiveAccount(cachedAccount);
    }
  }, [instance, isAuthenticated]);

  if (!isAuthenticated) return <Login />;

  const activeAccount = instance.getActiveAccount() || instance.getAllAccounts()[0];
  const reviewer = buildReviewer(activeAccount);
  const showUpload = allDevices.length === 0 || tab === "settings";
  const priceMap = { "Staff Device": devicePrice };
  const props = {
    computeDevices, allDevices: enrichedDevices,
    csvFiles, jiraMap, defenderMap,
    jiraLoading, jiraError, jiraLoadedAt, defenderLoading,
    handleCSV, clearData, loadJira, clearJira, showUpload,
    devicePrice, handlePriceChange, priceMap,
    configLoading, configSaving, configError, configLoadedAt,
    lwtLoading, lwtFallback, lwtStatus, lwtLoadedAt, lwtDeviceCount,
    reviewer,
  };

  const changeTab = nextTab => {
    if (nextTab !== tab && isReviewDirty && (tab === "devices" || tab === "archive-devices")) {
      if (!window.confirm("You have unsaved budget review changes. Leave without saving?")) return;
      setIsReviewDirty(false);
    }
    setTab(nextTab);
  };

  if (isLeadershipForecast) {
    return <LeadershipForecast {...props} computeDevices5Year={computeDevices5Year} />;
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg)" }}>
      {/* Sidebar */}
      <nav style={{ width: 200, background: "var(--bg2)", borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", flexShrink: 0 }}>
        <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid var(--border)" }}>
          <img src="/cwlogo.png" alt="Christway College" style={{ width: 140, display: "block", marginBottom: 10 }} />
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 17, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>Device Lifecycle Tool</div>
          <div style={{ fontSize: 12, color: "#2CFF05", marginTop: 3 }}>Built by John Vilar</div>
        </div>
        <div style={{ padding: "12px 10px", flex: 1 }}>
          {PRIMARY_NAV.map(n => {
            const active = tab === n.id;
            const activeBg = n.id === "inventory" ? "rgba(34,197,94,0.11)" : n.id.endsWith("-5") ? "rgba(245,158,11,0.10)" : "rgba(79,156,249,0.12)";
            return (
            <button key={n.id} onClick={() => changeTab(n.id)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 12px", background: active ? activeBg : "transparent", border: "none", borderRadius: 8, color: active ? n.color : "var(--muted)", cursor: "pointer", fontSize: 13, fontWeight: active ? 600 : 400, textAlign: "left", marginBottom: 2, transition: "all 0.15s" }}>
              <span style={{ fontSize: 14, opacity: 0.85, color: active ? n.color : "var(--muted)" }}>{n.icon}</span>
              <span style={{ display: "flex", flexDirection: "column", minWidth: 0, lineHeight: 1.15 }}>
                <span>{n.label}</span>
                <span style={{ fontSize: 9, color: active ? n.color : "var(--muted)", opacity: active ? 0.9 : 0.65, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "normal" }}>{n.context}</span>
              </span>
            </button>
          );})}
        </div>
        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", fontSize: 11, color: "var(--muted)" }}>
          <div>{computeDevices5Year.length} tracked devices</div>
          <button
            onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: "/" })}
            style={{ marginTop: 8, background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 10, padding: 0, textDecoration: "underline" }}
          >
            Sign out
          </button>
        </div>
      </nav>

      {/* Main content */}
      <main style={{ flex: 1, overflow: "auto", padding: "28px 32px" }}>
        {lwtLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(79,156,249,0.08)", border: "1px solid rgba(79,156,249,0.25)", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "var(--accent)", marginBottom: 20 }}>
            <span className="spinner" style={{ width: 14, height: 14, borderTopColor: "var(--accent)", flexShrink: 0 }} />
            Loading devices from LWT…
          </div>
        )}
        {!lwtLoading && lwtFallback && (
          <div style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "rgba(245,158,11,0.9)", marginBottom: 20, fontWeight: 600 }}>
            ⚠ Using cached data — LWT API unavailable
          </div>
        )}
        {tab.startsWith("archive-") && (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 10, padding: "12px 14px", marginBottom: 20 }}>
            <div>
              <strong style={{ display: "block", color: "var(--warn)", fontSize: 13 }}>Archived 4-Year Lifecycle</strong>
              <span style={{ color: "var(--muted)", fontSize: 11 }}>Historical reference only. The former device review is read-only and its saved records remain preserved.</span>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                { id: "archive-dashboard", label: "Dashboard" },
                { id: "archive-devices", label: "Devices" },
                { id: "archive-forecast", label: "Forecast" },
              ].map(item => (
                <button key={item.id} onClick={() => changeTab(item.id)} style={{ background: tab === item.id ? "rgba(245,158,11,0.18)" : "var(--bg2)", border: `1px solid ${tab === item.id ? "var(--warn)" : "var(--border)"}`, color: tab === item.id ? "var(--warn)" : "var(--muted)", padding: "6px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{item.label}</button>
              ))}
              <button onClick={() => changeTab("settings")} style={{ background: "var(--bg2)", border: "1px solid var(--border)", color: "var(--text)", padding: "6px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>Return to Settings</button>
            </div>
          </div>
        )}
        {tab === "dashboard" && <Dashboard {...props} computeDevices={computeDevices5Year} lifecycleYears={5} />}
        {tab === "devices"   && <DeviceList {...props} computeDevices={computeDevices5Year} lifecycleYears={5} reviewProgram="5-year" onDirtyChange={setIsReviewDirty} />}
        {tab === "forecast"  && <Forecast {...props} computeDevices={computeDevices5Year} lifecycleYears={5} reviewProgram="5-year" />}
        {tab === "archive-dashboard" && <Dashboard {...props} />}
        {tab === "archive-devices"   && <DeviceList {...props} readOnly />}
        {tab === "archive-forecast"  && <Forecast {...props} />}
        {tab === "inventory"  && <Inventory defenderMap={defenderMap} inventoryDevices={inventoryDevices} inventoryLoading={inventoryLoading} inventoryLoadedAt={inventoryLoadedAt} inventoryError={inventoryError} loadInventory={loadInventory} inventoryDefenderLoading={inventoryDefenderLoading} inventoryDefenderProgress={inventoryDefenderProgress} mdeSweepLoading={mdeSweepLoading} mdeSweepProgress={mdeSweepProgress} mdeSweepTotal={mdeSweepTotal} mdeSweepFound={mdeSweepFound} mdeSweepDone={mdeSweepDone} />}
        {tab === "eaptls"     && <EapTlsMigration />}
        {tab === "settings"   && <Settings {...props} onOpenArchivedLifecycle={() => changeTab("archive-dashboard")} />}
      </main>
    </div>
  );
}
